import { BillingAddonReferenceFragment } from 'generated/resources/graphql'

import getF25Plans from 'modules/upsell/lib/selfUpsell/plans/getF25Plans'
import {
  BUSINESS_VARIANT,
  ENTERPRISE_VARIANT,
  mapToF25Addon,
} from 'modules/upsell/lib/selfUpsell/plans/utils'

/**
 * Status of a marketplace module for the current company.
 *
 * Historically computed client-side. The authoritative answer now comes from
 * the backend `DatoModules` repository and is exposed via the GraphQL fields
 * `alreadyPurchased`, `installedVariant`, and `installedAddonInternalId` on
 * each module. This type is kept for compatibility with the
 * `useCheckSubscriptionItemsInstalled` hook which still returns it for the
 * checkout / cross-selling flow.
 */
export type ModuleStatus = {
  isInstalled: boolean
  variant: typeof BUSINESS_VARIANT | typeof ENTERPRISE_VARIANT | null
  installedAddonInternalId: string | null
  from: 'subscription' | 'bundle' | 'overload' | null
  hasEnterpriseVariant?: boolean | undefined
}

/**
 * Whether the module has an enterprise variant available for the given country.
 *
 * This is a catalog-level question, not an installation question: it asks
 * "is there an Enterprise SKU listed for this module in this country?", and
 * is used to decide whether the UI should offer an upgrade to Enterprise.
 */
export const hasEnterpriseVariant = (
  addonRef: BillingAddonReferenceFragment[],
  country: string
): boolean => {
  const plans = getF25Plans({ allF25Addons: mapToF25Addon(addonRef) }, country).f25Addons
  return plans.some((plan) => plan.getSpecificVariant(ENTERPRISE_VARIANT) != null)
}
